import * as React from "react";
import { styled, css } from "styled-components";
import { useModal } from "@ludens-reklame/react-modal";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { useIsDesktop } from "../../hooks/useIsDesktop.js";
import { ICompany, IPin } from "./data.js";
import {
  close,
  mapPin,
  mapPinDesktop,
} from "../../icons/index.js";
import { Modal } from "../../internal-components/Modal.js";

interface PinProps {
  pin: IPin;
  setActivePin: (id: string | null) => void;
}

export const Pin: React.FC<PinProps> = ({ pin, setActivePin }) => {
  const modal = useModal();
  const isDesktop = useIsDesktop({ mobileSize: 960 });

  const toggler = (e: React.MouseEvent) => {
    e.preventDefault();
    setActivePin(pin.id);

    modal.spawnModal(
      <Modal
        onClose={() => {
          modal.despawnModal();
          setActivePin(null);
        }}
      >
        <ModalHeader>
          <h3>{pin.label}</h3>
          <button
            onClick={() => {
              modal.despawnModal();
              setActivePin(null);
            }}
          >
            {close}
          </button>
        </ModalHeader>
        <ModalBody className="modal-body">
          {pin.companies.map((company, companyIndex) => {
            return <Company key={companyIndex} company={company} />;
          })}
        </ModalBody>
      </Modal>
    );
  };

  return (
    <Wrapper key={pin.id} className={`pin pin-${pin.id}`}>
      <PinContainer direction={pin.labelDirection} href="#" onClick={toggler}>
        <PinIcon className="pin-icon">
          {isDesktop ? mapPinDesktop : mapPin}
        </PinIcon>
        <PinLabel
          className="pin-label"
          direction={pin.labelDirection}
          active={pin.active}
        >
          {pin.label}
        </PinLabel>
      </PinContainer>
    </Wrapper>
  );
};

const Company: React.FC<{ company: ICompany }> = ({ company }) => {
  return (
    <div className="pin-company">
      <div className="img-container">
        <img src={company.logo} alt={company.name} />
      </div>
      <p>
        <span className="b2">Tlf:</span>{" "}
        <a href={`tel:${company.phone}`}>{company.phone}</a>
      </p>
      <p>
        <span className="b2">E-post:</span>{" "}
        <a href={`mailto:${company.email}`}>{company.email}</a>
      </p>
      <p>
        <span className="b2">Nettside:</span>{" "}
        <a
          href={`https://${company.website}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {company.website}
        </a>
      </p>
    </div>
  );
};

const Wrapper = styled.div`
  &.pin {
    position: absolute;
  }

  &.pin.pin-1 {
    bottom: 22.7%;
    left: 5.3%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 25.7%;
      left: 44.125%;
    }
  }

  &.pin.pin-2 {
    bottom: 8.4%;
    left: 11.5%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 10.3%;
      left: 48.7%;
    }
  }

  &.pin.pin-3 {
    bottom: 26.5%;
    right: 50.4%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 34.3%;
      right: 29.125%;
    }
  }

  &.pin.pin-4 {
    bottom: 27.7%;
    right: 40.6%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 36.6%;
      left: 82.125%;
    }
  }

  &.pin.pin-5 {
    bottom: 24.1%;
    right: 40.2%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 30.6%;
      right: 17.125%;
    }
  }

  &.pin.pin-6 {
    bottom: 22.4%;
    left: 60.3%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 28.3%;
      left: 74.4%;
    }
  }

  &.pin.pin-7 {
    bottom: 20%;
    right: 34.9%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 23.8%;
      left: 79.6%;
    }
  }

  &.pin.pin-8 {
    bottom: 9.5%;
    left: 49.2%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 10.9%;
      left: 71.8%;
    }
  }

  &.pin.pin-9 {
    bottom: 24.1%;
    left: 62.5%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 34.6%;
      left: 82.125%;
    }
  }

  &.pin.pin-10 {
    bottom: 26.7%;
    right: 35.6%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 33.3%;
      left: 83.4%;
    }
  }

  &.pin.pin-11 {
    bottom: 23.7%;
    left: 58.4%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 30.9%;
      left: 81.6%;
    }
  }

  &.pin.pin-12 {
    bottom: 17.5%;
    left: 60.5%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 21.6%;
      left: 78.5%;
    }
  }

  &.pin.pin-13 {
    bottom: 17.2%;
    left: 56%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 20.9%;
      left: 73.4%;
    }
  }

  &.pin.pin-14 {
    bottom: 13.7%;
    left: 58.3%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 17.6%;
      left: 77.4%;
    }
  }

  &.pin.pin-15 {
    bottom: 20.5%;
    right: 38%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 26.4%;
      left: 80.125%;
    }
  }
  &.pin.pin-16 {
    bottom: 66.9%;
    left: 71%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 77%;
      left: 84%;
    }
  }
  &.pin.pin-17 {
    bottom: 5.5%;
    left: 14%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 7.9%;
      left: 45%;
    }
  }
  &.pin.pin-18 {
    bottom: 2.8%;
    left: 17%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 5.7%;
      left: 48.8%;
    }
  }

  &.pin.pin-19 {
    bottom: 20%;
    right: 42%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 26%;
      right: 19%;
    }
  }

  &.pin.pin-20 {
    bottom: 43.5%;
    left: 16%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 50.5%;
      left: 50.5%;
    }
  }

  &.pin.pin-21 {
    bottom: 61%;
    left: 2%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 48.5%;
      left: 1%;
    }
  }

  &.pin.pin-22 {
    bottom: 59.5%;
    left: 6.5%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 45.6%;
      left: 3.5%;
    }
  }

  &.pin.pin-23 {
    bottom: 57.5%;
    left: 2%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 43.3%;
      left: 1%;
    }
  }

  &.pin.pin-24 {
    bottom: 63%;
    left: 4.5%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 51.3%;
      left: 3%;
    }
  }

  &.pin.pin-25 {
    bottom: 70.5%;
    left: 13%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 60.8%;
      left: 9.2%;
    }
  }

  &.pin.pin-26 {
    bottom: 20.5%;
    left: 20.2%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 23.8%;
      left: 53.2%;
    }
  }

  &.pin.pin-27 {
    bottom: 38.5%;
    left: 17%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 45.5%;
      left: 52.5%;
    }
  }

  &.pin.pin-28 {
    bottom: 15.2%;
    left: 44%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 19%;
      left: 61%;
    }
  }

  &.pin.pin-29 {
    bottom: 8%;
    left: 46%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: 9.4%;
      right: 26.5%;
    }
  }

  &.pin.pin-30 {
    bottom: 0%;
    left: 27%;

    @media (min-width: ${mobileBreakPointCSS}) {
      bottom: .5%;
      left: 58%;
    }
  }
`;

const PinContainer = styled.a<{ direction: IPin["labelDirection"] }>`
  display: flex;
  flex-direction: ${(props) =>
    props.direction === "left" ? "row-reverse" : "row"};
  position: relative;
  align-items: center;
  text-decoration: none;
`;

const PinIcon = styled.div`
  svg {
    width: 32px;
    height: 32px;
  }
`;

const PinLabel = styled.div<{
  direction: IPin["labelDirection"];
  active: IPin["active"];
}>`
  width: auto;
  background-color: #028db8;
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 400;
  color: #fff;
  position: relative;
  top: -12px;
  z-index: 2;
  display: none;

  border-radius: 3px;

  @media (min-width: ${mobileBreakPointCSS}) {
    display: inline-flex;
  }

  ${(props) =>
    props.active &&
    css`
      background-color: #ffffff;
      color: #028db8;
      border-color: #ffffff;
    `};

  ${(props) =>
    props.direction === "left" &&
    css`
      &::before {
        width: 0;
        height: 0;
        content: "";
        display: inline-block;
        border-top: 5px solid transparent;
        border-left: 4px solid ${props.active ? "#ffffff" : "#028db8"};
        border-bottom: 5px solid transparent;
        position: absolute;
        right: -4px;
      }
    `};

  ${(props) =>
    props.direction === "right" &&
    css`
      &::before {
        width: 0;
        height: 0;
        content: "";
        display: inline-block;
        border-top: 5px solid transparent;
        border-right: 4px solid ${props.active ? "#ffffff" : "#028db8"};
        border-bottom: 5px solid transparent;
        position: absolute;
        left: -4px;
      }
    `};
`;

export const PinContent = styled.div`
  background-color: #fff;
  color: var(--swatch2);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  padding: ${(props) => props.theme.spacing.m};
  position: absolute;
  z-index: 10;
  width: 350px;
  border-radius: 8px;

  img {
    width: 200px;
    object-fit: contain;
    display: inline-block;
  }

  a {
    color: var(--swatch2);
  }

  p {
    margin: 0;
  }

  .pin-company {
    &:not(:last-child) {
      border-bottom: 1px solid var(--swatch5);
      padding-bottom: ${(props) => props.theme.spacing.m};
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(props) => props.theme.spacing.s};
  background-color: var(--swatch1);
  color: #fff;
  padding: ${(props) => props.theme.spacing.s};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  h3 {
    margin: 0;
  }

  button {
    background: none;
    border: none;
    cursor: pointer;

    svg {
      height: 16px;
      width: 16px;

      path {
        fill: #fff;
      }
    }
  }
`;

const ModalBody = styled.div`
  background-color: #fff;
  color: var(--swatch2);
  width: 100%;
  box-sizing: border-box;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;

  max-height: 80vh;
  height: 100%;
  overflow: scroll;

  a {
    color: var(--swatch2);
  }

  .img-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  img {
    width: 100%;
    max-width: 400px;
    margin-bottom: ${(props) => props.theme.spacing.s};
  }

  .pin-company {
    padding: ${(props) => props.theme.spacing.m};

    p {
      margin: 0;
    }

    &:not(:last-child) {
      border-bottom: 1px solid var(--swatch2);
      padding-bottom: ${(props) => props.theme.spacing.m};
    }
  }
`;
